import type {
  AppExecutionDiagnostics,
  AppExecutionSnapshot,
  AppExecutionTraceEntry,
  AppTraceOutcome,
} from '@erp/json-renderer/apps/react';
import type { AppController } from './appController';
import type { AppBehaviorHost, BehaviorTraceEntry } from './appBehaviorHost';
import type { AppTransition } from './appInvocationLifecycle';

/**
 * One causal meaning for the inspector and for `read_app_live_state`.
 *
 * Rows are built from the controller's invocation log and the host's operation
 * log. Links are explicit ids (`invocationId`, `parentId`); nothing is
 * attributed by nearby timestamps. Both logs stay bounded and keep ownership
 * separate, so evicting a row never affects a write.
 */

export const EXECUTION_ROW_LIMIT = 60;

function invocationRow(event: AppTransition): AppExecutionTraceEntry | null {
  const id = `inv:${invocationIdOf(event)}`;
  switch (event.kind) {
    case 'action-requested':
      return {
        id: `${id}:requested`,
        at: event.at,
        chain: `command:${event.command}`,
        outcome: 'requested',
        summary: 'Requested the App command.',
        invocationId: event.invocationId,
        details: { params: event.params },
      };
    case 'action-rejected':
      return {
        id: `${id}:rejected`,
        at: event.at,
        chain: `command:${event.command}`,
        outcome: 'rejected',
        summary: `${event.reason}: ${event.message}`,
        invocationId: event.invocationId,
      };
    case 'action-canceled':
      return {
        id: `${id}:canceled`,
        at: event.at,
        chain: `command:${event.command}`,
        outcome: 'canceled',
        summary: `Canceled because ${event.reason}. The server may still finish the write.`,
        invocationId: event.invocationId,
        details: { remoteMayComplete: event.remoteMayComplete },
      };
    case 'result-ignored':
      return {
        id: `${id}:ignored`,
        at: event.at,
        chain: `command:${event.command}`,
        outcome: 'ignored',
        summary: 'A late result arrived after its consumer was replaced.',
        invocationId: event.invocationId,
      };
    case 'command-settled':
      return {
        id: `${id}:settled`,
        at: event.at,
        chain: `command:${event.command}`,
        outcome: settledOutcome(event.outcome),
        summary: settledSummary(event),
        invocationId: event.invocationId,
        details: {
          outcome: event.outcome,
          refreshWarnings: [...event.refreshWarnings],
          message: event.message,
          summary: event.summary,
        },
      };
    default:
      return null;
  }
}

function invocationIdOf(event: AppTransition): number {
  return 'invocationId' in event ? event.invocationId : 0;
}

function settledOutcome(
  outcome: 'success' | 'refresh' | 'transport' | 'command' | 'tool',
): AppTraceOutcome {
  if (outcome === 'success') return 'succeeded';
  if (outcome === 'refresh') return 'succeeded-with-warnings';
  return 'failed';
}

function settledSummary(event: {
  outcome: string;
  refreshWarnings: readonly string[];
  message: string | null;
}): string {
  if (event.outcome === 'refresh')
    return `Write applied; these feeds did not reload: ${event.refreshWarnings.join(', ')}.`;
  if (event.outcome !== 'success') return event.message ?? event.outcome;
  return 'Write applied.';
}

function behaviorRow(event: BehaviorTraceEntry): AppExecutionTraceEntry | null {
  if (event.kind === 'fact-emitted' || event.kind === 'fact-stopped')
    return factRow(event);
  if (event.kind === 'follow-up-failed' || event.kind === 'write-rejected')
    return failureRow(event);
  return operationRow(event);
}

function operationRow(
  event: BehaviorTraceEntry,
): AppExecutionTraceEntry | null {
  switch (event.kind) {
    case 'operation-requested':
      return requestedRow(event);
    case 'operation-settled':
      return settledRow(event);
    case 'operation-failed':
      return failedRow(event);
    default:
      return null;
  }
}

function factRow(
  event: Extract<BehaviorTraceEntry, { kind: 'fact-emitted' | 'fact-stopped' }>,
): AppExecutionTraceEntry {
  return event.kind === 'fact-emitted' ? emittedRow(event) : stoppedRow(event);
}

function failureRow(
  event: Extract<
    BehaviorTraceEntry,
    { kind: 'follow-up-failed' | 'write-rejected' }
  >,
): AppExecutionTraceEntry {
  return event.kind === 'follow-up-failed'
    ? followUpFailedRow(event)
    : writeRejectedRow(event);
}

function requestedRow(
  event: Extract<BehaviorTraceEntry, { kind: 'operation-requested' }>,
): AppExecutionTraceEntry {
  return {
    id: `op:${event.operationId}:requested`,
    at: event.at,
    chain: operationChain(event),
    outcome: 'requested',
    summary: event.alias
      ? `Action alias "${event.alias}" resolved to this operation.`
      : 'Operation started.',
    parentId: event.parentOperationId
      ? `op:${event.parentOperationId}:settled`
      : null,
    details: {
      input: event.input,
      sessionId: event.sessionId,
      origins: event.origins,
    },
  };
}

function settledRow(
  event: Extract<BehaviorTraceEntry, { kind: 'operation-settled' }>,
): AppExecutionTraceEntry {
  return {
    id: `op:${event.operationId}:settled`,
    at: event.at,
    chain: `${event.instance}.${event.operation}`,
    outcome: 'succeeded',
    summary:
      event.changedPaths.length > 0
        ? `Accepted state at ${event.changedPaths.join(', ')}.`
        : 'Completed without changing own state.',
    invocationId: event.invocationId,
    details: { changedPaths: [...event.changedPaths] },
  };
}

function failedRow(
  event: Extract<BehaviorTraceEntry, { kind: 'operation-failed' }>,
): AppExecutionTraceEntry {
  return {
    id: `op:${event.operationId}:failed`,
    at: event.at,
    chain: `${event.instance}.${event.operation}`,
    outcome: failedOrRejected(event.reason),
    summary: `${event.reason}: ${event.message}`,
    // A failed operation that reached its command still links to it.
    invocationId: event.invocationId ?? null,
    details: { reason: event.reason },
  };
}

/** Cancellation is not a rejection: the editor knows the write was abandoned. */
function failedOrRejected(reason: string): AppTraceOutcome {
  if (reason === 'failed') return 'failed';
  if (reason === 'canceled') return 'canceled';
  return 'rejected';
}

function emittedRow(
  event: Extract<BehaviorTraceEntry, { kind: 'fact-emitted' }>,
): AppExecutionTraceEntry {
  return {
    id: `op:${event.operationId}:fact:${event.event}`,
    at: event.at,
    chain: `${event.instance}.${event.event}`,
    outcome: 'info',
    summary: `Emitted with ${event.targets} target(s).`,
    parentId: `op:${event.operationId}:settled`,
    details: { payload: event.payload },
  };
}

function stoppedRow(
  event: Extract<BehaviorTraceEntry, { kind: 'fact-stopped' }>,
): AppExecutionTraceEntry {
  return {
    id: `op:${event.operationId}:stop:${event.event}`,
    at: event.at,
    chain: `${event.instance}.${event.event}`,
    outcome: 'stopped',
    summary: 'The policy is empty, so the fact stops here.',
    parentId: `op:${event.operationId}:settled`,
  };
}

function followUpFailedRow(
  event: Extract<BehaviorTraceEntry, { kind: 'follow-up-failed' }>,
): AppExecutionTraceEntry {
  return {
    id: `op:${event.operationId}:follow:${event.instance}.${event.operation}`,
    at: event.at,
    chain: `${event.instance}.${event.operation}`,
    outcome: 'failed',
    summary: event.message,
    parentId: `op:${event.operationId}:settled`,
  };
}

function writeRejectedRow(
  event: Extract<BehaviorTraceEntry, { kind: 'write-rejected' }>,
): AppExecutionTraceEntry {
  return {
    id: `write:${event.sequence}`,
    at: event.at,
    chain: event.path,
    outcome: 'rejected',
    summary: `Direct write rejected: ${event.reason}.`,
    details: { path: event.path, reason: event.reason },
  };
}

function operationChain(
  event: Extract<BehaviorTraceEntry, { kind: 'operation-requested' }>,
): string {
  return [
    originLabel(event.origins),
    event.alias,
    `${event.instance}.${event.operation}`,
  ]
    .filter(Boolean)
    .join(' -> ');
}

/**
 * An alias may be bound in more than one place, and the renderer reports only
 * params. Name one binding only when the plan declares one; otherwise say how
 * many it declares and leave the list in the row's details.
 */
function originLabel(
  origins: readonly { element: string; event: string; bindingPath: string }[],
): string | null {
  if (origins.length === 0) return null;
  if (origins.length > 1) return `${origins.length} declared bindings`;
  const [origin] = origins;
  return `${origin.element}.${origin.event} (${origin.bindingPath})`;
}

/**
 * Both logs as one bounded list. Ordering is display only; every link is an
 * explicit id, so a reordered or evicted row never changes attribution.
 */
export function executionRows(
  invocations: readonly AppTransition[],
  behaviors: readonly BehaviorTraceEntry[],
  limit = EXECUTION_ROW_LIMIT,
): AppExecutionTraceEntry[] {
  return executionTrace(invocations, behaviors, limit).rows;
}

/** The display rows plus how many the limit hid, so history cannot look whole. */
export function executionTrace(
  invocations: readonly AppTransition[],
  behaviors: readonly BehaviorTraceEntry[],
  limit = EXECUTION_ROW_LIMIT,
): { rows: AppExecutionTraceEntry[]; dropped: number } {
  // Ties keep each log's recorded order. Sorting by id would put
  // `inv:2:rejected` before `inv:2:requested`, and a small limit would return
  // the request while dropping the rejection.
  const merged = [
    ...invocations.flatMap((event) =>
      mergedRow(invocationRow(event), 0, event.sequence),
    ),
    ...behaviors.flatMap((event) =>
      mergedRow(behaviorRow(event), 1, event.sequence),
    ),
  ];
  merged.sort(
    (a, b) => a.at - b.at || a.source - b.source || a.sequence - b.sequence,
  );
  const rows = merged.slice(-Math.max(1, limit)).map((entry) => entry.row);
  return { rows, dropped: merged.length - rows.length };
}

interface MergedRow {
  readonly row: AppExecutionTraceEntry;
  readonly at: number;
  /** 0 for the controller's invocations, 1 for the host's operations. */
  readonly source: number;
  readonly sequence: number;
}

function mergedRow(
  row: AppExecutionTraceEntry | null,
  source: number,
  sequence: number,
): MergedRow[] {
  return row ? [{ row, at: row.at, source, sequence }] : [];
}

/**
 * The inspector's source. It recomputes only when one of the logs records
 * something, and returns the same snapshot until then.
 */
export function createExecutionDiagnostics(
  controller: AppController,
  host: AppBehaviorHost | null,
): AppExecutionDiagnostics {
  const listeners = new Set<() => void>();
  let revision = 0;
  let snapshot: AppExecutionSnapshot = { revision, rows: [], dropped: 0 };

  function refresh(): void {
    revision += 1;
    const invocations = controller.diagnostics.getSnapshot();
    const behaviors = host?.diagnostics.getSnapshot();
    const trace = executionTrace(
      invocations.events,
      behaviors?.events ?? [],
      EXECUTION_ROW_LIMIT,
    );
    snapshot = {
      revision,
      rows: trace.rows,
      dropped: invocations.dropped + (behaviors?.dropped ?? 0) + trace.dropped,
    };
    for (const listener of [...listeners]) listener();
  }

  // Owned by the page for the controller's lifetime; the inspector only adds
  // its own listener, so unmounting the panel stops its updates.
  controller.diagnostics.subscribe(refresh);
  host?.diagnostics.subscribe(refresh);
  refresh();

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      return snapshot;
    },
  };
}

/** The same bounded meaning for the assistant's `read_app_live_state`. */
export function appLiveState(
  controller: AppController,
  host: AppBehaviorHost | null,
  eventLimit = 20,
) {
  return {
    ...controller.inspect(eventLimit),
    behaviors: host?.inspect(eventLimit) ?? null,
    execution: executionRows(
      controller.diagnostics.getSnapshot().events,
      host?.diagnostics.getSnapshot().events ?? [],
      eventLimit,
    ),
  };
}
